import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import api from "../lib/axios";

// ─── Types ───────────────────────────────────────────────
export type OnboardingRole = "Admin" | "Manager" | "Cashier";

export interface StepDefinition {
  key: string;
  label: string;
}

export interface RoleOnboardingState {
  currentStep: number;
  completedSteps: boolean[];
  isComplete: boolean;
  isSkipped: boolean;
}

interface OnboardingSliceState {
  byRole: Partial<Record<OnboardingRole, RoleOnboardingState>>;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

// ─── Step config per role ────────────────────────────────
export const ONBOARDING_STEPS: Record<OnboardingRole, StepDefinition[]> = {
  Admin: [
    { key: "plan_selection", label: "Plan" },
    { key: "store_setup", label: "Store" },
    { key: "employees", label: "Employees" },
    { key: "inventory", label: "Inventory" },
    { key: "sales", label: "Sales" },
  ],
  Manager: [
    { key: "inventory", label: "Inventory" },
    { key: "employees", label: "Employees" },
    { key: "sales", label: "Sales" },
  ],
  Cashier: [
    { key: "sales", label: "Sales" },
    { key: "customers", label: "Customers" },
  ],
};

// ─── Helpers ─────────────────────────────────────────────
function defaultStateForRole(role: OnboardingRole): RoleOnboardingState {
  return {
    currentStep: 0,
    completedSteps: Array(ONBOARDING_STEPS[role].length).fill(false),
    isComplete: false,
    isSkipped: false,
  };
}

// ─── Async thunks ────────────────────────────────────────
export const fetchOnboarding = createAsyncThunk(
  "onboarding/fetch",
  async (role: OnboardingRole) => {
    const { data } = await api.get(`/onboarding?role=${role}`);
    return { role, ...data } as {
      role: OnboardingRole;
      current_step: number;
      completed_steps: boolean[];
      is_complete: boolean;
      is_skipped: boolean;
    };
  }
);

export const saveOnboarding = createAsyncThunk(
  "onboarding/save",
  async (
    payload: {
      role: OnboardingRole;
      current_step: number;
      completed_steps: boolean[];
      is_complete: boolean;
      is_skipped: boolean;
    }
  ) => {
    const { data } = await api.put("/onboarding", payload);
    return { role: payload.role, ...data } as {
      role: OnboardingRole;
      current_step: number;
      completed_steps: boolean[];
      is_complete: boolean;
      is_skipped: boolean;
    };
  }
);

// ─── Slice ───────────────────────────────────────────────
const initialState: OnboardingSliceState = {
  byRole: {},
  status: "idle",
  error: null,
};

function applyServerData(
  state: OnboardingSliceState,
  role: OnboardingRole,
  data: { current_step: number; completed_steps: boolean[]; is_complete: boolean; is_skipped: boolean }
) {
  state.byRole[role] = {
    currentStep: data.current_step,
    completedSteps: data.completed_steps,
    isComplete: data.is_complete,
    isSkipped: data.is_skipped,
  };
}

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    completeStep(state, action: PayloadAction<{ role: OnboardingRole; step: number }>) {
      const { role, step } = action.payload;
      const roleState = state.byRole[role] ?? defaultStateForRole(role);
      const stepCount = ONBOARDING_STEPS[role].length;

      if (step >= 0 && step < stepCount) {
        roleState.completedSteps[step] = true;
        if (step + 1 < stepCount) {
          roleState.currentStep = step + 1;
        }
      }
      state.byRole[role] = roleState;
    },

    setCurrentStep(state, action: PayloadAction<{ role: OnboardingRole; step: number }>) {
      const { role, step } = action.payload;
      const roleState = state.byRole[role] ?? defaultStateForRole(role);
      roleState.currentStep = step;
      state.byRole[role] = roleState;
    },

    skipOnboarding(state, action: PayloadAction<{ role: OnboardingRole }>) {
      const { role } = action.payload;
      const roleState = state.byRole[role] ?? defaultStateForRole(role);
      roleState.isSkipped = true;
      state.byRole[role] = roleState;
    },

    completeOnboarding(state, action: PayloadAction<{ role: OnboardingRole }>) {
      const { role } = action.payload;
      const roleState = state.byRole[role] ?? defaultStateForRole(role);
      roleState.isComplete = true;
      roleState.isSkipped = false;
      state.byRole[role] = roleState;
    },

    resetOnboarding(state, action: PayloadAction<{ role: OnboardingRole }>) {
      const { role } = action.payload;
      state.byRole[role] = defaultStateForRole(role);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOnboarding.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOnboarding.fulfilled, (state, action) => {
        state.status = "succeeded";
        applyServerData(state, action.payload.role, action.payload);
      })
      .addCase(fetchOnboarding.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to fetch onboarding";
      })
      .addCase(saveOnboarding.fulfilled, (state, action) => {
        applyServerData(state, action.payload.role, action.payload);
      });
  },
});

export const {
  completeStep,
  setCurrentStep,
  skipOnboarding,
  completeOnboarding,
  resetOnboarding,
} = onboardingSlice.actions;

// ─── Selectors ───────────────────────────────────────────
export const selectOnboardingForRole =
  (role: OnboardingRole) => (state: RootState) =>
    state.onboarding.byRole[role] ?? defaultStateForRole(role);

export const selectOnboardingStatus = (state: RootState) => state.onboarding.status;

export const onboardingReducer = onboardingSlice.reducer;
